import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { LoginState } from 'authentication/enums/login-state.enum';
import { EMPTY, Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { IS_SHIJI_USER_KEY, PASSWORD_CHANGE_EMAIL, PASSWORD_CHANGE_TOKEN, TOKEN_KEY } from 'shared/constants/general-constants';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';
import { Token } from 'shared/interfaces/token.interface';
import { UserCredentials } from 'shared/interfaces/user-credentials.interface';
import { EventBusService } from 'shared/services';
import { GlobalConfigService } from 'shared/services/global-config.service';
import { StorageService } from 'shared/services/storage.service';
import { ToastService } from 'shared/services/toast.service';
import { UserApiService } from 'shared/services/userApi.service';
import { TokenService } from './token.service';

const AUTH_API = environment.BASE_URL + 'userApi/user/';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private _userLoggedInRequestedPasswordChange = false;
    private _userLoggedInTwoFactorAuthenticationEnforced = false;
    private _isForceLogout = false;

    constructor(
        private http: HttpClient,
        private router: Router,
        private tokenService: TokenService,
        private storageService: StorageService,
        private eventBus: EventBusService,
        private toastService: ToastService,
        private userApiService: UserApiService,
        private globalConfigService: GlobalConfigService,
    ) {}

    public login(userCredentials: UserCredentials): Observable<unknown> {
        return this.http
            .post<Token>(this.getLoginUrl(), userCredentials, {
                context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [406, 420, 423, 457]),
            })
            .pipe(
                switchMap((token) => {
                    this.loginAndSetToken(token);

                    // We need the intermediary token on the token interceptor for the next request
                    return this.getUserToken(token, userCredentials);
                }),
                tap(() => this.userApiService.getCurrentUser()),
                catchError((error) => {
                    // Invalid login attempt (REST call)
                    if (error.status === 406) {
                        this.toastService.showToast({
                            body: 'generalKeywords.invalidLoginAttempt',
                            severity: 'error',
                            duration: 8000,
                            ignoreRemoval: true,
                        });
                        return EMPTY;
                    }

                    // 2FA required
                    if (error.status === 457) {
                        this.loginAndSetToken(error.error);
                        this._userLoggedInTwoFactorAuthenticationEnforced = true;
                        return of(LoginState.TWO_FACTOR_AUTHENTICATION_ENFORCED);
                    }

                    // Password change was required by an admin
                    if (this.isStatusPasswordChangeRequired(error)) {
                        return this.handlePasswordChangeRequired(error, userCredentials);
                    }

                    // Account disabled
                    if (error.networkError?.status === 423) {
                        this.toastService.showToast({
                            body: 'generalKeywords.accountDisabled',
                            severity: 'error',
                            duration: 8000,
                        });
                        this.storageService.clearLocal();
                        this.storageService.clearSession();

                        return EMPTY;
                    }

                    throw error;
                }),
            );
    }

    private isStatusPasswordChangeRequired(error: any) {
        return (error.networkError?.status ?? error.status) === 420;
    }

    private handlePasswordChangeRequired(error: any, userCredentials?: UserCredentials) {
        this._userLoggedInRequestedPasswordChange = true;
        if (error && error.error && error.error.token) {
            this.tokenService.setToken(error.error.token);
        }
        const currentToken = this.storageService.getFromLocal(TOKEN_KEY);
        this.storageService.removeFromLocal(TOKEN_KEY);
        this.storageService.setInSession(PASSWORD_CHANGE_TOKEN, currentToken);
        this.storageService.setInSession(PASSWORD_CHANGE_EMAIL, userCredentials?.email ?? '');
        this.router.navigate(['change-password']);

        return EMPTY;
    }

    public sendOtp(value: string | undefined): Observable<Token> {
        return this.http
            .post<Token>(
                AUTH_API + 'two-fa-signin',
                {
                    twoFactorCode: value,
                },
                {
                    headers: new HttpHeaders({ isBypassTokenInterceptorRetryRequest: 'true' }),
                    context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [456]),
                },
            )
            .pipe(
                switchMap(() => {
                    // Fake the token object
                    const token = {
                        token: this.tokenService.token()!,
                        isShijiUser: this.userApiService.isShiji,
                    };

                    this.loginAndSetToken(token);

                    // We need the intermediary token on the token interceptor for the next request
                    return this.getUserToken(token);
                }),
                tap(() => this.userApiService.getCurrentUser()),
            );
    }

    public loginAndSetToken(result: Token) {
        this._userLoggedInRequestedPasswordChange = false;
        this._userLoggedInTwoFactorAuthenticationEnforced = false;
        // We save this for the request to "getUserToken()" that needs the identityToken
        this.tokenService.setToken(result.token);
        this.tokenService.setIdentityToken(result.token);
        // The other requests will use the userToken which is set in userApiService
        this.userApiService.isShiji = result.isShijiUser;
        this.storageService.setInLocal(IS_SHIJI_USER_KEY, result.isShijiUser);
    }

    public getUserToken(identityToken: Token, userCredentials?: UserCredentials, redirectToLoginOnError: boolean = false) {
        return this.userApiService.getUserToken(identityToken.isShijiUser, redirectToLoginOnError).pipe(
            catchError((error: HttpErrorResponse) => {
                // If the error status code is 420 (Requires password change) we redirect the user to the change password page
                if (this.isStatusPasswordChangeRequired(error)) {
                    this.handlePasswordChangeRequired(error, userCredentials);
                }

                throw error;
            }),
            // This is the actual proper token that we need to use going forward
            tap((token) => this.tokenService.setToken(token)),
        );
    }

    public isLoggedIn(): boolean {
        return this.storageService.getFromLocal(TOKEN_KEY) != null && !this._userLoggedInTwoFactorAuthenticationEnforced;
    }

    public get isForceLogout(): boolean {
        return this._isForceLogout;
    }

    public set isForceLogout(value: boolean) {
        this._isForceLogout = value;
    }

    public isUserLoggedInRequestedPasswordChange(): boolean {
        return this._userLoggedInRequestedPasswordChange;
    }

    public isUserLoggedInTwoFactorAuthenticationEnforced(): boolean {
        return this._userLoggedInTwoFactorAuthenticationEnforced;
    }

    public logout(returnUrl?: string): void {
        this.storageService.clearSession();
        this.storageService.clearLocal();

        this.router.navigate(['/login'], { queryParams: { returnUrl: returnUrl } }).then((response: any) => {
            if (response) {
                this.tokenService.setToken(undefined);
                this.tokenService.setIceDashboardToken(undefined);
                this.tokenService.setIdentityToken(undefined);
                this.userApiService.removeCurrentUser();
                this.globalConfigService.removeLanguages();
                this.globalConfigService.removeSupplierClasses();
                this.eventBus.dispatch('logout');
                this.isForceLogout = false;
            }
        });
    }

    public getLoginUrl() {
        return AUTH_API + 'signin';
    }

    public redirectToIcePortal(): Observable<{ RedirectUrl: string; EncryptedData: string }> {
        return this.http.get<{ RedirectUrl: string; EncryptedData: string }>(AUTH_API + 'redirect-to-ice-portal', {
            headers: new HttpHeaders({
                isBypassTokenInterceptorRetryRequest: 'true',
            }),
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [401]),
        });
    }

    public renewIdentityTokenAsObservable(): Observable<string> {
        return this.http
            .get<{ value: string }>(AUTH_API + 'renew-identity-token', {
                headers: new HttpHeaders({ isBypassTokenInterceptorRetryRequest: 'true' }),
            })
            .pipe(
                map((response) => response.value),
                tap((token: string) => {
                    this.tokenService.setIdentityToken(token);
                }),
            );
    }

    public requestNewOtpEmail() {
        return this.http.post(
            AUTH_API + 'regenerate-code',
            {},
            {
                headers: new HttpHeaders({ isBypassTokenInterceptorRetryRequest: 'true' }),
                context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [456]),
            },
        );
    }

    public getNewTwoFactorIdentityToken() {
        return this.http.post<string>(AUTH_API + 'two-fa-renew-token', {}).pipe(map((token: any) => token.value));
    }
}
